import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  FiberManualRecord as FiberManualRecordIcon
} from '@material-ui/icons';
import InlineTextPopover from 'components/@common/InlineTextPopover';
import { ProductCurriculum } from 'types/schema-generated-types';
import { isPrmSetACurriculum } from '../../common/curriculum-util';
import { percentOf } from '../../common/SplitProgressBarChart/util';
import StudentLargeProgressBar from './StudentLargeProgressBar';
import StudentSupportBar from './StudentSupportBar';

type Props = {
  productCurriculum?: ProductCurriculum | null;
  completedAndMet: any;
  usedSupports: any;
  didNotUseSupports: any;
  total: any;
  unmet: any;
};

const colors = {
  primary: {
    met: '#51A486',
    notMet: '#EA031F'
  },
  secondary: {
    met: '#FFC20E',
    notMet: '#919191'
  }
};

const StudentLargeBarChart: React.FC<Props> = ({
  productCurriculum,
  completedAndMet,
  usedSupports,
  didNotUseSupports,
  total,
  unmet
}) => {
  const classes = useStyles();
  const { t } = useTranslation('ReportsHelpCommon');

  const completedTitleText = 'Completed Lessons';
  const completedTitle = isPrmSetACurriculum(productCurriculum) ? (
    <InlineTextPopover
      PopoverContentProps={{ description: t('prmSetAReporting') }}
      style={DEFAULT_TITLE_STYLE}
    >
      {completedTitleText}
    </InlineTextPopover>
  ) : (
    <Title>{completedTitleText}</Title>
  );

  return (
    <Box display="flex" flex={1} flexDirection="column">
      <StudentLargeProgressBar
        unmet={unmet}
        total={total}
        metColor={colors.primary.met}
        notMetColor={colors.primary.notMet}
        metDescription={<Description text="Skills Met" />}
        MetEndAdornment={
          <CheckCircleIcon
            fontSize="small"
            color="inherit"
            classes={{ root: `${classes.icon} ${classes.check}` }}
          />
        }
        notMetDescription={<Description text="Skills Not Met" />}
        NotMetEndAdornment={
          <CancelIcon fontSize="small" color="error" classes={{ root: classes.icon }} />
        }
        title={completedTitle}
        completedAndMet={completedAndMet}
      />

      <Box width={'100%'}>
        {completedAndMet > 0 && (
          <StudentSupportBar
            width={`${percentOf(completedAndMet, completedAndMet + unmet)}`}
            supports={usedSupports}
            didNotUseSupports={didNotUseSupports}
            completed={completedAndMet}
            metColor={colors.secondary.met}
            notMetColor={colors.secondary.met}
            metDescription={<Description text="Needed Support Path" />}
            MetEndAdornment={
              <FiberManualRecordIcon
                style={{
                  fontSize: 12,
                  alignSelf: 'center',
                  color: '#FFC20E'
                }}
              />
            }
            notMetDescription={<Description text="Did not need Support Path" />}
            title="Supports Provided"
          />
        )}
      </Box>
    </Box>
  );
};

const DEFAULT_TITLE_STYLE = { fontSize: 24 };

const Title = ({
  children,
  style = DEFAULT_TITLE_STYLE
}: {
  children: string;
  style?: React.CSSProperties;
}) => <Typography style={style}>{children}</Typography>;

const Description = ({ text }: { text: string }) => (
  <Typography variant="h6" style={{ fontWeight: 400 }}>
    {text}
  </Typography>
);

const useStyles = makeStyles(() => ({
  icon: {
    fontSize: 12,
    alignSelf: 'center',
    marginLeft: 4
  },
  check: {
    color: '#51A486'
  }
}));

export default StudentLargeBarChart;
